import React, { useState } from 'react';
import { useRegister } from '../../hooks/use-register';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { Helmet } from 'react-helmet-async';
import InputComponent from '../../components/input/input.component';
import ErrorValidateComponent from '../../components/error-validate/error-validate.component';
import { Link } from 'react-router-dom';
import { routePaths } from '../../routes/routes-path';
import ButtonPrimary from '../../components/button-refactor/button-primary';
import Alert from '../../components/alert/alert';
import { API } from '../../api';

const registerSchema = yup.object().shape({
  email: yup.string().email('Email không hợp lệ').required('Email không được bỏ trống'),
  // Tên đăng nhập cũng là đường dẫn trang cá nhân (cab.vn/user/tên) nên theo cùng luật với đường dẫn tuỳ chỉnh.
  username: yup
    .string()
    .required('Tên đăng nhập không được bỏ trống')
    .matches(/^[a-zA-Z0-9_]{3,30}$/, 'Tên đăng nhập dài 3–30 ký tự, chỉ gồm chữ không dấu, số và dấu gạch dưới.'),
  fullName: yup.string().required('Tên không được bỏ trống'),
  password: yup.string().required('Mật khẩu không được bỏ trống').min(8, 'Mật khẩu phải có ít nhất 8 ký tự').matches(/\d/, 'Mật khẩu phải có ít nhất 1 chữ số'),
  confirmPassword: yup
    .string()
    .required('Nhập lại mật khẩu không khớp')
    .oneOf([yup.ref('password'), ''], 'Nhập lại mật khẩu không khớp'),
});

export interface IRegisterFormValues {
  email: string;
  fullName: string;
  username: string;
  password: string;
  confirmPassword: string;
}

export const RegisterPage = () => {
  const [registerSuccess, setRegisterSuccess] = useState<boolean>(false);
  const [registeredEmail, setRegisteredEmail] = useState<string>('');

  const onRegisterSuccess = () => {
    setRegisterSuccess(true);
  };

  const { handleRegister, loading } = useRegister(onRegisterSuccess);

  const {
    handleSubmit,
    register,
    setError,
    formState: { errors },
  } = useForm<IRegisterFormValues>({
    mode: 'all',
    resolver: yupResolver(registerSchema),
  });

  const onSubmit: SubmitHandler<IRegisterFormValues> = async (formValues) => {
    setRegisteredEmail(formValues.email);
    await handleRegister(formValues);
  };

  return (
    <div className="toto-register">
      <Helmet>
        <title>Đăng ký</title>
        <meta name="register" content="Description of register" />
      </Helmet>

      <div className="min-h-screen flex justify-center items-center p-2">
        <div className="w-full px-5 lg:px-0 mx-auto py-8">
          <div className="rounded-md bg-transparent md:bg-slate-50 dark:bg-zinc-800 max-w-md mx-auto p-6 shadow-lg">
            <h2 className="text-2xl font-bold mb-8 text-center">Đăng ký tài khoản</h2>

            <form className="mb-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-4">
                <label className="block font-medium text-sm mb-2 " htmlFor="email">
                  Email
                </label>

                <InputComponent register={register('email')} type="email" id="email" />
                <ErrorValidateComponent visible={!!errors.email}>{errors.email?.message}</ErrorValidateComponent>
              </div>
              <div className="mb-4">
                <label className="block font-medium text-sm mb-2 " htmlFor="fullName">
                  Họ và Tên
                </label>
                <InputComponent register={register('fullName')} id="fullName" />
                <ErrorValidateComponent visible={!!errors.fullName}>{errors.fullName?.message}</ErrorValidateComponent>
              </div>
              <div className="mb-4">
                <label className="block font-medium text-sm mb-2 " htmlFor="username">
                  Tên đăng nhập
                </label>
                <InputComponent
                  register={register('username', {
                    // Báo trùng ngay khi rời ô (trước đây 2 người đặt trùng tên được, server giờ cũng chặn).
                    onBlur: async (e) => {
                      const value = String(e.target.value || '').trim();
                      if (!/^[a-zA-Z0-9_]{3,30}$/.test(value)) return;
                      try {
                        const { data } = await API.get<{ available: boolean; message?: string }>(
                          'v1/user-service/public/username-available',
                          { params: { username: value } }
                        );
                        if (!data.available) setError('username', { message: data.message || 'Tên đăng nhập này đã có người dùng.' });
                      } catch {
                        // Không kiểm được thì để server kiểm lúc bấm Đăng ký.
                      }
                    },
                  })}
                  id="username"
                />
                <ErrorValidateComponent visible={!!errors.username}>{errors.username?.message}</ErrorValidateComponent>
              </div>
              <div className="mb-4">
                <label className="block font-medium text-sm mb-2 " htmlFor="password">
                  Mật khẩu
                </label>
                <InputComponent register={register('password')} id="password" type="password" />
                <ErrorValidateComponent visible={!!errors.password}>{errors.password?.message}</ErrorValidateComponent>
              </div>
              <div className="mb-4">
                <label className="block font-medium text-sm mb-2 " htmlFor="confirmPassword">
                  Nhập lại mật khẩu
                </label>
                <InputComponent register={register('confirmPassword')} id="confirmPassword" type="password" />
                <ErrorValidateComponent visible={!!errors.confirmPassword}>
                  {errors.confirmPassword?.message}
                </ErrorValidateComponent>
              </div>
              {/* Đăng ký xong thì thay nút bằng hướng dẫn: trước đây thông báo nằm lẫn trong form và tắt
                  khi bấm lại, người dùng tưởng chưa được nên bấm lần nữa (lỗi trùng email, 08/10). */}
              {registerSuccess ? (
                <Alert visible type="success" classNames="mb-4">
                  Đăng ký thành công. Chúng tôi đã gửi mail xác nhận tới <b>{registeredEmail}</b>. Bạn mở hộp thư, xem
                  cả mục Thư rác, rồi bấm vào đường dẫn trong mail để kích hoạt tài khoản, sau đó{' '}
                  <Link to={routePaths.login} className="font-semibold underline">
                    đăng nhập
                  </Link>
                  .
                </Alert>
              ) : (
                <ButtonPrimary type="submit" className="w-full justify-center" loading={loading} disabled={loading}>
                  Đăng ký
                </ButtonPrimary>
              )}
            </form>

            <div className="mb-8">
              <p className="text-sm text-slate-800 dark:text-gray-100">
                Bạn đã có tài khoản?{' '}
                <Link to={routePaths.login} className="text-primary-5 font-semibold">
                  Đăng nhập
                </Link>
              </p>
            </div>

            <p className="text-xs text-center text-zinc-500 dark:text-zinc-300 mb-4">
              Bằng việc đăng kí tài khoản, bạn đã đồng ý với{' '}
              <a className="text-primary-6 font-medium " href="/" target="_blank">
                Điều khoản sử dụng
              </a>{' '}
              và đã đọc{' '}
              <a className="text-primary-6 font-medium " href="/" target="_blank">
                Chính sách quyền riêng tư
              </a>{' '}
              của chúng tôi.
            </p>
            <p className="text-xs text-center text-zinc-500 dark:text-zinc-300" data-svelte-h="svelte-sgs55j">
              ©{new Date().getFullYear()} cab.vn
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
